import React, { Component, ErrorInfo, ReactNode } from 'react';
import { LocalStorageManager } from '../../services/localStorage';
import './common.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onGroupContextLost?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isGroupContextError: boolean;
}

class GroupContextErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isGroupContextError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is a group context related error
    const isGroupContextError = GroupContextErrorBoundary.isGroupContextError(error);
    
    return {
      hasError: true,
      error,
      isGroupContextError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('GroupContextErrorBoundary caught error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // If this is a group context error, handle it appropriately
    if (this.state.isGroupContextError) {
      this.handleGroupContextError(error);
    }
  }

  private static isGroupContextError(error: Error): boolean {
    const errorMessage = error.message.toLowerCase();
    const errorStack = error.stack?.toLowerCase() || '';
    
    // Check for group context related error patterns
    const groupContextPatterns = [
      'group context',
      'group slug',
      'gruppo non trovato',
      'non hai accesso a questo gruppo',
      'contesto del gruppo',
      'invalid group',
      'group not found',
      'unauthorized group access',
      'missing group context',
      'x-group-slug',
    ];

    return groupContextPatterns.some(pattern => 
      errorMessage.includes(pattern) || errorStack.includes(pattern)
    );
  }

  private handleGroupContextError = (error: Error) => {
    console.warn('Group context error detected, clearing invalid group data:', error.message);
    
    try {
      // Clear potentially invalid group data from localStorage
      LocalStorageManager.clearGroupPreference();
      
      // Store error message for display on group selection page
      sessionStorage.setItem('groupSelectionError', 'Si è verificato un problema con il contesto del gruppo');
      
      // Call the callback if provided
      if (this.props.onGroupContextLost) {
        this.props.onGroupContextLost();
      } else {
        // Default behavior: redirect to group selection
        this.redirectToGroupSelection();
      }
    } catch (cleanupError) {
      console.error('Failed to cleanup after group context error:', cleanupError);
      // Still try to redirect even if cleanup fails
      this.redirectToGroupSelection();
    }
  };

  private redirectToGroupSelection = () => {
    // Avoid infinite redirects - don't redirect from public pages
    const currentPath = window.location.pathname;
    const { isPublicRoute } = require('../../constants/routes');
    
    if (!isPublicRoute(currentPath)) {
      // Use replace to avoid adding to history
      window.location.replace('/group-selection');
    }
  };

  private handleRetry = () => {
    if (this.state.isGroupContextError) {
      // For group context errors, redirect to group selection instead of retrying
      this.redirectToGroupSelection();
    } else {
      // For other errors, allow retry
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isGroupContextError: false,
      });
    }
  };

  private handleGoToGroupSelection = () => {
    this.redirectToGroupSelection();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      if (this.state.isGroupContextError) {
        return (
          <div className="error-boundary">
            <div className="error-boundary-content">
              <h2>Problema con il gruppo</h2>
              <p>
                Si è verificato un problema con il contesto del gruppo. 
                Verrai reindirizzato alla selezione del gruppo per risolvere il problema.
              </p>
              
              <div className="error-actions">
                <button 
                  onClick={this.handleGoToGroupSelection}
                  className="btn btn-primary"
                >
                  Vai alla selezione gruppo
                </button>
                <button 
                  onClick={() => window.location.reload()}
                  className="btn btn-secondary"
                >
                  Ricarica la pagina
                </button>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="error-details">
                  <summary>Dettagli errore (solo sviluppo)</summary>
                  <pre className="error-stack">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        );
      }

      // For non-group-context errors, show standard error boundary
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h2>Oops! Qualcosa è andato storto</h2>
            <p>Si è verificato un errore imprevisto. Ci scusiamo per l'inconveniente.</p>
            
            <div className="error-actions">
              <button 
                onClick={this.handleRetry}
                className="btn btn-primary"
              >
                Riprova
              </button>
              <button 
                onClick={() => window.location.reload()}
                className="btn btn-secondary"
              >
                Ricarica la pagina
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Dettagli errore (solo sviluppo)</summary>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GroupContextErrorBoundary;